"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export function PropertyContactForm() {
  const [contactMethod, setContactMethod] = useState<"call" | "video">("call");

  return (
    <section className="py-24 px-6 md:px-12 max-w-[1400px] mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Left Content */}
        <div>
          <div className="space-y-4 mb-8">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-secondary block">
              We'd Love To
            </span>
            <h2 className="font-serif text-4xl md:text-6xl text-primary">
              Hear From <span className="italic text-primary/50">You</span>
            </h2>
          </div>

          <div className="space-y-4">
            <h5 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Starting Prices</h5>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              AED 9.32 M* | INR 22.7 CR* | USD 2.55 M* | EUR 2.25 M* | GBP 1.96 M*
            </p>
            <p className="text-[10px] text-muted-foreground/60 mt-4">
              *Subject to inventory availability<br />
              *The global prices may vary as per the exchange rate
            </p>
          </div>
        </div>

        {/* Right Form */}
        <form className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div
              className={cn(
                "flex-1 p-4 border rounded-sm cursor-pointer flex items-center justify-between transition-all",
                contactMethod === "call" ? "border-primary bg-primary/5" : "border-input hover:border-primary/50"
              )}
              onClick={() => setContactMethod("call")}
            >
              <span className={cn("text-sm font-medium", contactMethod === "call" ? "text-primary" : "text-muted-foreground")}>
                Request a call back
              </span>
              <div className={cn("w-4 h-4 rounded-full border flex items-center justify-center", contactMethod === "call" ? "border-primary" : "border-muted-foreground")}>
                {contactMethod === "call" && <div className="w-2 h-2 rounded-full bg-primary" />}
              </div>
            </div>

            <div
              className={cn(
                "flex-1 p-4 border rounded-sm cursor-pointer flex items-center justify-between transition-all",
                contactMethod === "video" ? "border-primary bg-primary/5" : "border-input hover:border-primary/50"
              )}
              onClick={() => setContactMethod("video")}
            >
              <span className={cn("text-sm font-medium", contactMethod === "video" ? "text-primary" : "text-muted-foreground")}>
                Schedule a video call
              </span>
              <div className={cn("w-4 h-4 rounded-full border flex items-center justify-center", contactMethod === "video" ? "border-primary" : "border-muted-foreground")}>
                {contactMethod === "video" && <div className="w-2 h-2 rounded-full bg-primary" />}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Input placeholder="First Name *" className="h-12 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Input placeholder="Last Name *" className="h-12 rounded-sm" />
            </div>
          </div>

          <div className="grid grid-cols-[100px_1fr] gap-4">
            <Select defaultValue="+971">
              <SelectTrigger className="h-12 rounded-sm">
                <SelectValue placeholder="Code" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="+971">🇦🇪 +971</SelectItem>
                <SelectItem value="+1">🇺🇸 +1</SelectItem>
                <SelectItem value="+44">🇬🇧 +44</SelectItem>
                <SelectItem value="+91">🇮🇳 +91</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="XX XXX XXXX" className="h-12 rounded-sm" />
          </div>

          <Input placeholder="Email *" className="h-12 rounded-sm" type="email" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input placeholder="The Grove" disabled className="h-12 rounded-sm bg-muted/50" />
            <Select>
              <SelectTrigger className="h-12 rounded-sm">
                <SelectValue placeholder="Unit Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4br">4 Bedroom Villa</SelectItem>
                <SelectItem value="5br">5 Bedroom Villa</SelectItem>
                <SelectItem value="6br">6 Bedroom Villa</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-start space-x-2">
              <Checkbox id="terms" />
              <Label htmlFor="terms" className="text-xs leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                I've read and agree to the <span className="underline cursor-pointer">privacy policy</span>. *
              </Label>
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox id="marketing" />
              <Label htmlFor="marketing" className="text-xs leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                I'd like to hear about news and offers.
              </Label>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full md:w-auto rounded-xl px-8 h-10 uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 mt-4"
          >
            Submit
          </Button>

          <p className="text-[10px] text-muted-foreground text-center md:text-left pt-4">
            This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
          </p>
        </form>
      </div>
    </section>
  );
}
